import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';
import { authAPI, restaurantAPI } from '../services/api';
import notificationService from '../services/notificationService';
import socketService from '../services/socket';
import { signOutFirebase } from '../config/firebase';

const AuthContext = createContext(null);

// Subscription helper functions
const getSubscriptionHelpers = (restaurant) => {
  if (!restaurant) {
    return {
      hasActiveSubscription: false,
      requiresSubscriptionAction: false,
      isFirstTimeSubscription: true,
    };
  }

  const subscriptionPlan = restaurant.subscriptionPlan;
  const isApproved = restaurant.isApproved && restaurant.approvalStatus === 'approved';

  // Check if restaurant has an active subscription (active or trial status)
  const hasActiveSubscription = (() => {
    if (!subscriptionPlan || subscriptionPlan.type !== 'monthly') {
      return false;
    }
    const status = subscriptionPlan.monthly?.status;
    return status === 'active' || status === 'trial';
  })();

  // Check if this is a returning subscriber (has payment history or used trial)
  // Returning subscribers should NOT be redirected to plan-selection when expired
  // They will see a renewal banner on Dashboard and have subscription auto-deducted from payouts
  const isReturningSubscriber = (() => {
    if (!subscriptionPlan || subscriptionPlan.type !== 'monthly') return false;
    const monthly = subscriptionPlan.monthly;
    return monthly?.isTrialUsed === true ||
           (monthly?.paymentHistory && monthly.paymentHistory.length > 0);
  })();

  // Check if restaurant requires subscription action (new subscription or renewal)
  const requiresSubscriptionAction = (() => {
    if (!isApproved) return false;
    // No plan selected yet
    if (!subscriptionPlan || subscriptionPlan.type === 'none') {
      return true;
    }
    // Only monthly subscription is valid now - any other type requires subscription
    if (subscriptionPlan.type !== 'monthly') {
      return true; // Legacy plans (one_time, commission) require new monthly subscription
    }
    // Monthly plan - check if active or needs renewal
    const status = subscriptionPlan.monthly?.status;

    // IMPORTANT: Returning subscribers with expired status should NOT be redirected
    // They can access dashboard and see renewal banner - subscription will auto-deduct from payouts
    if (isReturningSubscriber && (status === 'expired' || status === 'cancelled')) {
      return false; // Allow access to dashboard, show renewal banner there
    }

    return status === 'pending' || status === 'expired' || status === 'cancelled';
  })();

  // Check if this is first-time subscription (eligible for free trial)
  const isFirstTimeSubscription = (() => {
    if (!subscriptionPlan || subscriptionPlan.type === 'none') {
      return true;
    }
    // Legacy plans (one_time, commission) are NOT first-time - they already had subscription
    if (subscriptionPlan.type !== 'monthly') {
      return false; // No free trial for legacy plan holders
    }
    // For monthly plans, check if trial was already used
    return subscriptionPlan.monthly?.isTrialUsed !== true;
  })();

  return {
    hasActiveSubscription,
    requiresSubscriptionAction,
    isFirstTimeSubscription,
  };
};

// ============================================
// Firebase Phone OTP Authentication (Primary)
// ============================================
// Note: Password-based login/register have been removed.
// All authentication now uses Firebase Phone OTP.
// ============================================

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const response = await authAPI.getProfile();
        const userData = response.data.data.user;
        setUser(userData);

        // If user is a restaurant, also fetch restaurant data
        if (userData.role === 'restaurant') {
          try {
            const restaurantResponse = await restaurantAPI.getMyRestaurant();
            setRestaurant(restaurantResponse.data.data.restaurant);
          } catch (err) {
            console.log('Restaurant not created yet or error fetching:', err);
            setRestaurant(null);
          }

          // Register FCM token for push notifications on page refresh
          // Run in background - don't block the auth flow
          const registerFCMToken = async (retryCount = 0) => {
            console.log(`🔔 FCM: Starting registration attempt ${retryCount + 1}...`);
            try {
              // Check notification permission first
              if (!('Notification' in window)) {
                console.log('🔔 FCM: Notifications not supported in this browser');
                return;
              }
              console.log(`🔔 FCM: Notification permission status: ${Notification.permission}`);

              // Skip if permission not granted (user hasn't opted in yet)
              if (Notification.permission !== 'granted') {
                console.log('🔔 FCM: Permission not granted, skipping FCM registration');
                return;
              }

              // Check service worker support
              if (!('serviceWorker' in navigator)) {
                console.log('🔔 FCM: Service workers not supported');
                return;
              }

              console.log('🔔 FCM: Getting token (Firebase will register service worker)...');
              const tokenPromise = notificationService.getToken(true);
              const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('FCM token request timeout')), 20000)
              );
              await Promise.race([tokenPromise, timeoutPromise]);
              console.log('🔔 FCM: Token registered successfully on session restore');
            } catch (err) {
              console.log(`🔔 FCM: Attempt ${retryCount + 1} failed:`, err?.message || err);
              // Retry up to 2 more times with increasing delay
              if (retryCount < 2) {
                const delay = (retryCount + 1) * 3000; // 3s, 6s
                console.log(`🔔 FCM: Retrying in ${delay/1000}s...`);
                setTimeout(() => registerFCMToken(retryCount + 1), delay);
              } else {
                console.log('🔔 FCM: All attempts failed. Push notifications may not work.');
              }
            }
          };

          // Start FCM registration after 2 seconds
          setTimeout(() => registerFCMToken(0), 2000);
        }
      } catch (error) {
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  };

  /**
   * Login or register using Firebase OTP
   * @param {string} idToken - Firebase ID token from OTP verification
   * @param {string} role - User role for new registrations ('user', 'restaurant', 'delivery')
   * @param {object} options - Additional options (loginOnly: true prevents auto-adding roles)
   * @returns {object} - { user, isNewUser, requiresProfileCompletion }
   */
  const loginWithOTP = async (idToken, role = 'user', options = {}) => {
    const response = await authAPI.verifyFirebaseToken(idToken, role, options);
    const {
      user,
      token,
      accessToken,
      refreshToken,
      isNewUser,
      requiresProfileCompletion
    } = response.data.data;

    // Store tokens
    localStorage.setItem('token', accessToken || token);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }

    setUser(user);

    // If user is a restaurant, fetch restaurant data
    let hasRestaurant = false;
    if (user.role === 'restaurant') {
      try {
        const restaurantResponse = await restaurantAPI.getMyRestaurant();
        const restaurantData = restaurantResponse.data.data.restaurant;
        setRestaurant(restaurantData);
        // Check if restaurant actually exists and has completed onboarding
        hasRestaurant = !!(restaurantData && restaurantData._id);
      } catch (err) {
        console.log('Restaurant not created yet or error fetching:', err);
        setRestaurant(null);
        hasRestaurant = false;
      }

      // Register FCM token for push notifications.
      //
      // Previously this was fired via `setTimeout(... , 2000)` and never
      // awaited — if the user logged in and immediately navigated away, the
      // token registration could fire against an unmounted context or race
      // with logout. Now we await registration with a hard timeout so
      // slow / failed service-worker boots don't block login indefinitely.
      //
      // `Promise.race` between the real registration and a 3-second
      // fallback ensures the function returns promptly either way.
      try {
        await Promise.race([
          (async () => {
            // Small delay so the service worker finishes registering.
            await new Promise(resolve => setTimeout(resolve, 500));
            await notificationService.getToken(true);
            console.log('FCM token registered after OTP login');
          })(),
          new Promise(resolve => setTimeout(() => {
            console.log('FCM token registration timed out (3s) — continuing');
            resolve();
          }, 3000)),
        ]);
      } catch (err) {
        console.log('Failed to register FCM token (non-blocking):', err?.message);
      }
    }

    return { user, isNewUser, requiresProfileCompletion, hasRestaurant };
  };

  /**
   * Complete user profile after OTP registration
   * @param {object} data - { name, email? }
   */
  const completeProfile = async (data) => {
    const response = await authAPI.completeRegistration(data);
    const { user: updatedUser } = response.data.data;
    setUser(updatedUser);
    return updatedUser;
  };

  /**
   * Check if phone number is already registered
   * @param {string} phone - Phone number to check
   */
  const checkPhone = async (phone) => {
    const response = await authAPI.checkPhone(phone);
    return response.data.data;
  };

  const logout = async () => {
    // Unregister FCM token before logout
    try {
      await notificationService.unregisterTokenFromServer();
      console.log('FCM token unregistered on logout');
    } catch (err) {
      console.log('Failed to unregister FCM token:', err);
    }

    // Disconnect the realtime socket so the logged-out client stops
    // receiving order / call / subscription events for the previous user.
    // Previously this leaked — the socket stayed connected with the old
    // token and kept firing handlers after logout.
    try {
      socketService.disconnect();
      console.log('🔌 Socket disconnected on logout');
    } catch (err) {
      console.log('Failed to disconnect socket on logout:', err);
    }

    // Sign out from Firebase (for OTP users)
    try {
      await signOutFirebase();
      console.log('Signed out from Firebase');
    } catch (err) {
      console.log('Failed to sign out from Firebase:', err);
    }

    // Clear local storage tokens (all keys to be safe)
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setUser(null);
    setRestaurant(null);
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getProfile();
      setUser(response.data.data.user);

      // If user is a restaurant, also refresh restaurant data
      if (response.data.data.user.role === 'restaurant') {
        try {
          const restaurantResponse = await restaurantAPI.getMyRestaurant();
          setRestaurant(restaurantResponse.data.data.restaurant);
        } catch (err) {
          console.log('Restaurant not created yet or error fetching:', err);
          setRestaurant(null);
        }
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  // Get subscription helpers for the current restaurant.
  // Memoised so that a context consumer using only auth fields (e.g.
  // ProtectedRoute reading `user.role`) doesn't re-evaluate because of
  // a stale `subscriptionStatus` object identity changing on every render.
  const subscriptionStatus = useMemo(
    () => getSubscriptionHelpers(restaurant),
    [restaurant]
  );

  // Memoise the entire context value so that identity-based React consumers
  // (and React.memo'd children that take the context as a prop) skip
  // re-renders when the actual fields haven't changed. Without this,
  // EVERY call to setUser/setRestaurant forced every subscriber to
  // re-render even if their slice was untouched.
  const contextValue = useMemo(() => ({
    user,
    restaurant,
    logout,
    loading,
    refreshUser,
    // OTP authentication methods (primary)
    loginWithOTP,
    completeProfile,
    checkPhone,
    ...subscriptionStatus,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [user, restaurant, loading, subscriptionStatus]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
